import type { Maybe } from '@adornis/base/utilTypes.js';
import type { NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import { toCalendarDay } from '@adornis/datetime/calendarDay.js';
import type { ContractSupplement, ContractSupplementInnerSection } from '../entities/ContractSupplement.js';

export type SupplementActiveSectionsSelectionSet = {
  signedAt: 1;
  startDateGeneral: 1;
  endDateGeneral: 1;
  startDateAG1: 1;
  endDateAG1: 1;
  startDateAG2: 1;
  endDateAG2: 1;
};

/**
 * Checks if the given nachtrag is active at the given point in time.
 * The Nachtrag can have three different inner sections, which can be active independent from one another. This function returns the active inner sections at the given point in time.
 * @param supplement The supplement to check.
 * @param time The time to check.
 * @returns The different sections which are actually active. For example the ag1 can be active at the given point but not the general section
 */
export function getActiveSectionsForPointInTime(
  supplement: NarrowedEntity<ContractSupplement, SupplementActiveSectionsSelectionSet>,
  time: Date,
  options?: {
    includeDrafts?: boolean;
    overrideActiveCheck?: boolean;
    forcedEnddate?: Maybe<Date>;
  },
): ContractSupplementInnerSection[] {
  const activeSections: ContractSupplementInnerSection[] = [];

  // supplement runtimes are inclusive canonical calendar days; normalize the reference instant once
  const day = toCalendarDay(time);
  const forcedEnddate = options?.forcedEnddate;

  // if we include drafts we ignore of the employee has already signed the contract
  const signedActive = !!supplement.signedAt || !!options?.includeDrafts;

  const capAtForcedEnddate = (endDate: Maybe<Date>): Maybe<Date> => {
    if (!endDate || !forcedEnddate) return endDate ?? forcedEnddate;
    return endDate > forcedEnddate ? forcedEnddate : endDate;
  };

  const endDateGeneral = capAtForcedEnddate(supplement.endDateGeneral);
  const endDateAG1 = capAtForcedEnddate(supplement.endDateAG1);
  const endDateAG2 = capAtForcedEnddate(supplement.endDateAG2);

  // check date intervalls and add the group to the array if active
  if (
    options?.overrideActiveCheck ||
    (supplement.startDateGeneral &&
      supplement.startDateGeneral <= day &&
      (!endDateGeneral || endDateGeneral >= day) &&
      signedActive)
  )
    activeSections.push('general');

  if (
    options?.overrideActiveCheck ||
    (supplement.startDateAG1 && supplement.startDateAG1 <= day && (!endDateAG1 || endDateAG1 >= day) && signedActive)
  )
    activeSections.push('ag1');

  if (
    options?.overrideActiveCheck ||
    (supplement.startDateAG2 && supplement.startDateAG2 <= day && (!endDateAG2 || endDateAG2 >= day) && signedActive)
  )
    activeSections.push('ag2');

  return activeSections;
}
